import React, { useState, useEffect } from 'react';
import { fetchSetting, updateSetting, recalcParcaFiyatlariFromIskonto } from '../services/settingsService';
import { useConfirmDialog } from '../contexts/ConfirmContext';

/**
 * @param {{ variant?: 'page' | 'embedded' }} [props] — `embedded`: Ayarlar hub içinde
 */
const IskontoAyarPage = ({ variant = 'page' }) => {
  const embedded = variant === 'embedded';
  const { confirm } = useConfirmDialog();
  const [iskontoOrani, setIskontoOrani] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadIskontoOrani();
  }, []);

  const loadIskontoOrani = async () => {
    try {
      setLoading(true);
      const response = await fetchSetting('ISKONTO_ORANI');

      if (response?.basarili && response?.veri?.setting) {
        const d = response.veri.setting.deger;
        setIskontoOrani(d !== undefined && d !== null && d !== '' ? String(d) : '0');
      } else {
        setIskontoOrani('0');
      }
    } catch (error) {
      console.error('İskonto oranı yüklenemedi:', error);
      setIskontoOrani('0');
      setMessage({ type: 'info', text: 'İskonto oranı henüz ayarlanmamış. Varsayılan 0 kullanılır.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const deger = parseFloat(String(iskontoOrani).replace(',', '.'));
    if (Number.isNaN(deger) || deger < 0 || deger > 1000) {
      setMessage({ type: 'error', text: 'Lütfen 0 ile 1000 arasında geçerli bir sayı girin' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await updateSetting('ISKONTO_ORANI', {
        deger,
        aciklama:
          'Parça içeriklerinde net fiyat (fiyatgaranti): brüt fiyat (fiyat) × (1 − oran/100). İşlemlerde iskonto uygulanmaz.',
        tip: 'number',
      });

      if (response?.basarili) {
        setMessage({ type: 'success', text: 'İskonto oranı başarıyla güncellendi!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: response?.mesaj || 'Güncelleme başarısız' });
      }
    } catch (error) {
      console.error('İskonto oranı güncellenemedi:', error);
      setMessage({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRecalcParcaFiyatlari = async () => {
    const oranText = String(iskontoOrani).replace('.', ',');
    const ok = await confirm({
      title: 'Parça fiyatlarını güncelle',
      message:
        `Tüm parça kayıtlarında brüt fiyat (fiyat) aynı kalacak; net fiyat (fiyatgaranti) güncel iskonto oranı (%${oranText}) ile yeniden hesaplanacak. Devam etmek istiyor musunuz?`,
      variant: 'warning',
      confirmLabel: 'Evet, güncelle',
    });
    if (!ok) return;

    try {
      setRecalculating(true);
      setMessage({ type: '', text: '' });
      const response = await recalcParcaFiyatlariFromIskonto();
      if (response?.basarili) {
        const count = response?.veri?.guncellenen ?? 0;
        setMessage({
          type: 'success',
          text: response.mesaj || `${count} kaydın net fiyatı güncellendi.`,
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      } else {
        setMessage({ type: 'error', text: response?.mesaj || 'Güncelleme başarısız' });
      }
    } catch (error) {
      console.error('Parça fiyatları güncellenemedi:', error);
      setMessage({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    const loader = (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: '#6b7280' }}>Yükleniyor...</p>
      </div>
    );
    if (embedded) return loader;
    return (
      <div className="page-container">
        <div className="content-header">
          <div>
            <h1>İskonto Ayarı</h1>
          </div>
        </div>
        {loader}
      </div>
    );
  }

  const card = (
      <div className="card" style={{ maxWidth: '700px', margin: embedded ? 0 : '0 auto' }}>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="iskontoOrani"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                color: '#374151',
                fontSize: '14px',
              }}
            >
              <i className="fas fa-percent" style={{ marginRight: '8px', color: '#3b82f6' }}></i>
              İskonto oranı (%)
            </label>
            <input
              type="number"
              id="iskontoOrani"
              value={iskontoOrani}
              onChange={(e) => setIskontoOrani(e.target.value)}
              placeholder="Örn: 30"
              step="0.01"
              min="0"
              max="1000"
              required
              disabled={saving}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <small
              style={{
                display: 'block',
                marginTop: '6px',
                fontSize: '13px',
                color: '#6b7280',
              }}
            >
              Örnek: Brüt fiyat 1000 ₺ ve oran %30 ise net fiyat 700 ₺ olur (1000 × 0,70).
            </small>
          </div>

          {message.text && (
            <div
              style={{
                padding: '12px 16px',
                marginBottom: '20px',
                borderRadius: '6px',
                backgroundColor:
                  message.type === 'success' ? '#d1fae5' : message.type === 'error' ? '#fee2e2' : '#dbeafe',
                color:
                  message.type === 'success' ? '#065f46' : message.type === 'error' ? '#991b1b' : '#1e40af',
                border: `1px solid ${message.type === 'success' ? '#10b981' : message.type === 'error' ? '#ef4444' : '#3b82f6'}`,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <i
                className={`fas fa-${message.type === 'success' ? 'check-circle' : message.type === 'error' ? 'exclamation-circle' : 'info-circle'}`}
              ></i>
              <span>{message.text}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: saving ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!saving) e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                if (!saving) e.target.style.backgroundColor = '#3b82f6';
              }}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Kaydet
                </>
              )}
            </button>
            <button
              type="button"
              disabled={recalculating || saving}
              onClick={handleRecalcParcaFiyatlari}
              style={{
                padding: '12px 24px',
                backgroundColor: recalculating ? '#9ca3af' : '#fff',
                color: recalculating ? '#fff' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: recalculating || saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {recalculating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt"></i>
                  Parça fiyatlarını güncelle
                </>
              )}
            </button>
          </div>
        </form>

        <div
          style={{
            marginTop: '32px',
            padding: '20px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}
        >
          <h3
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
            Bilgilendirme
          </h3>
          <ul
            style={{
              margin: 0,
              paddingLeft: '24px',
              fontSize: '14px',
              color: '#4b5563',
              lineHeight: '1.8',
            }}
          >
            <li style={{ marginBottom: '8px' }}>
              Parça eklerken/düzenlerken girilen tutar brüt fiyattır; net fiyat sunucuda bu oranla hesaplanır.
              İşlemlerde garanti dahili/harici fiyatlar manuel girilir, iskonto uygulanmaz.
            </li>
            <li style={{ marginBottom: '8px' }}>
              İskonto oranını değiştirdikten sonra <strong>Parça fiyatlarını güncelle</strong> butonuna basmazsanız
              eski parçalar eski net fiyatlarını korur. Tüm mevcut parçaları yeni orana göre hizalamak için bu butonu kullanın.
            </li>
          </ul>
        </div>
      </div>
  );

  if (embedded) return card;

  return (
    <div className="page-container">
      <div className="content-header">
        <div>
          <h1>İskonto Ayarı</h1>
        </div>
      </div>
      {card}
    </div>
  );
};

export default IskontoAyarPage;
